import { AppDispatch } from '../store'
import heatmapActionTypes from '../actionTypes/heatmap.actionTypes'
import { getHeatmapConfiguration, getHeatmapSecurities } from '../services/apis/heatmap'
import {
    HeatmapConfiguration,
    HeatmapSecuritiesResponse,
    HeatmapGroupBy,
} from '../proto/generated/screener'

/** Monotonic id so only the latest securities response is applied (avoids stale overwrites). */
let securitiesRequestId = 0

const heatmapActions = {
    fetchConfig: async (dispatch: AppDispatch): Promise<HeatmapConfiguration | null> => {
        dispatch({ type: heatmapActionTypes.SET_CONFIG_LOADING, payload: true })
        try {
            const config = await getHeatmapConfiguration()
            dispatch({ type: heatmapActionTypes.SET_CONFIG, payload: config })
            return config
        } catch (err: any) {
            dispatch({ type: heatmapActionTypes.SET_ERROR, payload: err?.message || 'Failed to load heatmap config' })
            return null
        }
    },

    fetchSecurities: async (
        dispatch: AppDispatch,
        args: {
            index?: string
            sector?: string | null
            groupBy: HeatmapGroupBy
            sizeBy?: string
            colorBy?: string
        }
    ): Promise<HeatmapSecuritiesResponse | null> => {
        const index = args.index?.trim() || undefined
        const sector = args.sector?.trim() || undefined

        // Backend requires at least one of index or sector — skip invalid requests.
        if (!index && !sector) {
            dispatch({ type: heatmapActionTypes.SET_DATA, payload: null })
            dispatch({ type: heatmapActionTypes.SET_ERROR, payload: 'Select an index or a sector' })
            return null
        }

        const requestId = ++securitiesRequestId
        dispatch({ type: heatmapActionTypes.SET_LOADING, payload: true })
        try {
            const request = {
                groupBy: args.groupBy,
                ...(index ? { index } : {}),
                ...(sector ? { sector } : {}),
                ...(args.sizeBy ? { sizeBy: args.sizeBy } : {}),
                ...(args.colorBy ? { colorBy: args.colorBy } : {}),
            }
            const data = await getHeatmapSecurities(request)
            if (requestId !== securitiesRequestId) return null
            dispatch({ type: heatmapActionTypes.SET_DATA, payload: data })
            return data
        } catch (err: any) {
            if (requestId !== securitiesRequestId) return null
            dispatch({ type: heatmapActionTypes.SET_ERROR, payload: err?.message || 'Failed to load heatmap data' })
            return null
        }
    },

    setIndex: (dispatch: AppDispatch, index: string) => {
        dispatch({ type: heatmapActionTypes.SET_INDEX, payload: index })
    },

    setSector: (dispatch: AppDispatch, sector: string | null) => {
        dispatch({ type: heatmapActionTypes.SET_SECTOR, payload: sector })
    },

    setGroupBy: (dispatch: AppDispatch, groupBy: HeatmapGroupBy) => {
        dispatch({ type: heatmapActionTypes.SET_GROUP_BY, payload: groupBy })
    },

    setSizeBy: (dispatch: AppDispatch, sizeBy: string) => {
        dispatch({ type: heatmapActionTypes.SET_SIZE_BY, payload: sizeBy })
    },

    setColorBy: (dispatch: AppDispatch, colorBy: string) => {
        dispatch({ type: heatmapActionTypes.SET_COLOR_BY, payload: colorBy })
    },
}

export default heatmapActions
